/**
 * Browser-tab unread indicator (Slack-style).
 *
 * Two complementary signals so an unread notification is noticeable even when
 * Code Concierge is in a background tab:
 *   1. A "(N)" prefix on the document title.
 *   2. A red count badge composited onto the favicon via an offscreen canvas.
 *
 * Both are no-ops / best-effort: missing canvas support (jsdom), a tainted
 * canvas, or a missing icon link must never throw into the React render path.
 */

// The title with any existing "(N)" prefix stripped, captured once at load.
const BASE_TITLE =
  (typeof document !== 'undefined' ? document.title : 'Code Concierge').replace(
    /^\(\d+\+?\)\s*/,
    '',
  ) || 'Code Concierge'

interface IconLink {
  link: HTMLLinkElement
  originalHref: string
}

let iconLinks: IconLink[] | null = null
let baseImage: HTMLImageElement | null = null
let baseImageLoading: Promise<void> | null = null

/** All <link rel="icon"> elements (excludes apple-touch-icon), cached. */
function getIconLinks(): IconLink[] {
  if (iconLinks) return iconLinks
  if (typeof document === 'undefined') return []
  const links = Array.from(
    document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]'),
  ).filter((l) => !(l.getAttribute('rel') || '').includes('apple-touch'))
  iconLinks = links.map((link) => ({ link, originalHref: link.href }))
  return iconLinks
}

/** Load the original favicon into an Image once, for canvas compositing. */
function loadBaseImage(): Promise<void> {
  if (baseImage) return Promise.resolve()
  if (baseImageLoading) return baseImageLoading
  const links = getIconLinks()
  if (!links.length || typeof Image === 'undefined') return Promise.resolve()

  baseImageLoading = new Promise<void>((resolve) => {
    const img = new Image()
    // Same-origin asset: do NOT set crossOrigin — the favicon is already cached
    // by the browser without CORS headers, and a CORS-enabled re-fetch can fail
    // to load (or never fire events). Same-origin images don't taint the canvas.
    let settled = false
    const done = () => {
      if (settled) return
      settled = true
      resolve()
    }
    img.onload = () => {
      baseImage = img
      done()
    }
    img.onerror = () => done()
    // Safety net: never hang the badge if the image neither loads nor errors;
    // we'll just draw the badge without the base icon.
    setTimeout(done, 3000)
    img.src = links[0].originalHref
  })
  return baseImageLoading
}

/** Restore every icon link to its original (un-badged) href. */
function clearFavicon() {
  for (const { link, originalHref } of getIconLinks()) {
    if (link.href !== originalHref) link.href = originalHref
  }
}

/** Trace a capsule/pill path (rounded rect with semicircular ends). */
function tracePill(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  radius: number,
) {
  const rr = Math.min(radius, w / 2, h / 2)
  ctx.beginPath()
  const c = ctx as CanvasRenderingContext2D & {
    roundRect?: (x: number, y: number, w: number, h: number, r: number) => void
  }
  if (typeof c.roundRect === 'function') {
    c.roundRect(x, y, w, h, rr)
    return
  }
  // Fallback for engines without CanvasRenderingContext2D.roundRect.
  ctx.moveTo(x + rr, y)
  ctx.arcTo(x + w, y, x + w, y + h, rr)
  ctx.arcTo(x + w, y + h, x, y + h, rr)
  ctx.arcTo(x, y + h, x, y, rr)
  ctx.arcTo(x, y, x + w, y, rr)
  ctx.closePath()
}

function drawBadgedFavicon(count: number): string | null {
  if (typeof document === 'undefined') return null
  const size = 64
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  if (baseImage) {
    ctx.drawImage(baseImage, 0, 0, size, size)
  }

  // Telegram-style corner badge: a red capsule — a circle for a single digit,
  // growing leftward into a pill for 2+ characters — wrapped in a white contrast
  // ring so it reads against any favicon.
  const label = count > 99 ? '99+' : String(count)
  const chars = label.length

  const h = size * 0.5 // capsule height (= circle diameter for one digit)
  const extraPerChar = h * 0.4 // pill growth per character beyond the first
  const w = h + (chars - 1) * extraPerChar
  const ring = size * 0.06 // white contrast-ring thickness
  const inset = size * 0.04
  const y = inset
  // Pin to the top-right corner, but keep the whole ring on-canvas.
  const x = Math.max(ring / 2, size - inset - w)
  const cx = x + w / 2
  const cy = y + h / 2

  // White contrast ring (a slightly larger capsule behind the red one).
  tracePill(ctx, x - ring / 2, y - ring / 2, w + ring, h + ring, (h + ring) / 2)
  ctx.fillStyle = '#ffffff'
  ctx.fill()

  // Red capsule.
  tracePill(ctx, x, y, w, h, h / 2)
  ctx.fillStyle = '#ff3b30' // bright iOS/Telegram-style red
  ctx.fill()

  // White, bold, centered count.
  ctx.fillStyle = '#ffffff'
  ctx.font = `bold ${h * (chars >= 3 ? 0.46 : 0.6)}px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(label, cx, cy + h * 0.04)

  try {
    return canvas.toDataURL('image/png')
  } catch {
    // Tainted canvas (shouldn't happen for same-origin) — skip.
    return null
  }
}

/** Prefix the tab title with the unread count, or restore the base title. */
export function setTitleBadge(count: number) {
  if (typeof document === 'undefined') return
  document.title = count > 0 ? `(${count > 99 ? '99+' : count}) ${BASE_TITLE}` : BASE_TITLE
}

/** Composite a red count badge onto the favicon, or restore it when count is 0. */
export async function setFaviconBadge(count: number) {
  if (count <= 0) {
    clearFavicon()
    return
  }
  if (typeof document === 'undefined') return
  // Probe canvas support up front so environments without it (e.g. jsdom)
  // bail before we await image loading rather than hanging on it.
  if (!document.createElement('canvas').getContext('2d')) return
  await loadBaseImage()
  const dataUrl = drawBadgedFavicon(count)
  if (!dataUrl) return
  for (const { link } of getIconLinks()) {
    link.href = dataUrl
  }
}

/**
 * Mirror the unread count onto the installed-PWA icon via the Badging API
 * (`navigator.setAppBadge` / `clearAppBadge`). Best-effort: it only has a
 * visible effect when the app is installed (desktop Chrome/Edge, Android;
 * iOS 16.4+ installed PWAs), is a silent no-op in a normal browser tab, and
 * must never throw into render. The OS renders its own dot/count, so the
 * exact number we pass is just a hint.
 */
export function setAppIconBadge(count: number) {
  if (typeof navigator === 'undefined') return
  const nav = navigator as Navigator & {
    setAppBadge?: (contents?: number) => Promise<void>
    clearAppBadge?: () => Promise<void>
  }
  try {
    if (count > 0) {
      nav.setAppBadge?.(count)?.catch(() => {})
    } else {
      nav.clearAppBadge?.()?.catch(() => {})
    }
  } catch {
    /* best-effort — never surface a Badging API failure to the caller */
  }
}

/** Drive the title, favicon, and installed-app-icon indicators from the count. */
export function applyTabBadge(count: number) {
  setTitleBadge(count)
  void setFaviconBadge(count)
  setAppIconBadge(count)
}

// Test-only hook to reset module caches between cases.
export function __resetTabBadgeForTest() {
  iconLinks = null
  baseImage = null
  baseImageLoading = null
}
